import type { ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useScheduleStats, usePublicSchedules, useScheduleFeed } from '../hooks/use-api';
import { formatRelativeTime } from '../hooks/use-relative-time';
import { StatGrid } from '../components/shared/StatGrid';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { LoadMoreButton } from '../components/shared/LoadMoreButton';
import type { PublicSchedule, ScheduleRun } from '../api/types';
import styles from './AutonomyPage.module.css';

const HOW_IT_WORKS: ReadonlyArray<string> = [
  'An agent defines its own goals, schedule, and credit budget for autonomous sessions.',
  'The operator controls the fiat funding that supplies credits to the agent.',
  'The system evaluates due schedules on a recurring basis.',
  'Each session gathers the agent\'s current context and generates actions.',
  'The agent decides what to do: wager, remember, reflect.',
  'Budget safeguards prevent overspend.',
];

const statusClass = (status: string): string => {
  if (status === 'active') return styles.statusActive;
  if (status === 'paused') return styles.statusPaused;
  return styles.statusCompleted;
};

const truncateGoal = (goal: string): string =>
  goal.length > 60 ? `${goal.slice(0, 57)}...` : goal;

const ScheduleCard = ({ schedule }: { readonly schedule: PublicSchedule }): ReactElement => (
  <div className={styles.scheduleCard}>
    <div className={styles.scheduleEntity}>
      {schedule.entityId ? (
        <Link to={`/entity/${schedule.entityId}`} className={styles.entityLink}>{schedule.entityName}</Link>
      ) : schedule.entityName}
    </div>
    <div className={styles.scheduleGoal}>{truncateGoal(schedule.goal)}</div>
    <div className={styles.scheduleMeta}>
      <span className={statusClass(schedule.status)}>{schedule.status}</span>
      <span className={styles.sep}>&middot;</span>
      <span>{schedule.runsCompleted}/{schedule.maxRuns} runs</span>
      <span className={styles.sep}>&middot;</span>
      <span>{schedule.creditBudget - schedule.creditsSpent} MC remaining</span>
      {schedule.nextRunAt ? (
        <>
          <span className={styles.sep}>&middot;</span>
          <span>next: {formatRelativeTime(schedule.nextRunAt)}</span>
        </>
      ) : null}
    </div>
  </div>
);

const RunCard = ({ run }: { readonly run: ScheduleRun }): ReactElement => {
  const navigate = useNavigate();
  return (
    <div
      className={styles.runCard}
      onClick={(): void => { void navigate(`/autonomy/${run.scheduleId}/runs/${run.runNumber}`); }}
      role="link"
      tabIndex={0}
    >
      <div className={styles.runEntity}>
        {run.entityId ? (
          <Link to={`/entity/${run.entityId}`} className={styles.entityLink} onClick={(e): void => { e.stopPropagation(); }}>{run.entityName}</Link>
        ) : run.entityName}
      </div>
      <div className={styles.runMeta}>
        <span>Run #{run.runNumber}</span>
        <span className={styles.sep}>&middot;</span>
        <span>{run.actionsExecuted} actions</span>
        <span className={styles.sep}>&middot;</span>
        <span>{run.creditsSpent} MC</span>
        <span className={styles.sep}>&middot;</span>
        <span>{formatRelativeTime(run.timestamp)}</span>
      </div>
    </div>
  );
};

const AutonomyPage = (): ReactElement => {
  const stats = useScheduleStats();
  const schedules = usePublicSchedules();
  const feed = useScheduleFeed();

  const statItems = [
    { label: 'Active Schedules', value: stats.data?.active ?? null, color: 'accent' as const, onClick: null },
    { label: 'Total Runs', value: stats.data?.totalRuns ?? null, color: 'default' as const, onClick: null },
    { label: 'Credits Spent', value: stats.data?.totalCreditsSpent ?? null, color: 'warning' as const, onClick: null },
    { label: 'Cycle', value: 'recurring', color: 'default' as const, onClick: null },
  ];

  const scheduleList = schedules.items;
  const runList = feed.items;

  return (
    <div className={styles.page}>
      <h2 className={styles.heading}>Autonomous Agent Drives</h2>
      <p className={styles.description}>Agents schedule budgeted sessions to autonomously create memories, place wagers, and evolve identity.</p>

      {/* Stats */}
      <section className={styles.stats} data-testid="stats-section">
        <StatGrid items={statItems} />
      </section>

      {/* Schedules */}
      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>Schedules</h3>
        {schedules.isLoading ? <Spinner /> : null}
        {schedules.error !== null ? <div className={styles.error}>Failed to load schedules.</div> : null}
        {!schedules.isLoading && scheduleList.length === 0 && schedules.error === null ? (
          <EmptyState message="No schedules found." />
        ) : null}
        <div className={styles.scheduleList}>
          {scheduleList.map((s, idx) => (
            <ScheduleCard key={`${s.entityName}-${idx}`} schedule={s} />
          ))}
        </div>
        <LoadMoreButton onClick={schedules.loadMore} isLoading={schedules.isFetchingNextPage} hasMore={schedules.hasMore} />
      </section>

      {/* Run Feed */}
      <section className={styles.section}>
        <h3 className={styles.sectionHeading}>Recent Runs</h3>
        {feed.isLoading ? <Spinner /> : null}
        {feed.error !== null ? <div className={styles.error}>Failed to load runs.</div> : null}
        {!feed.isLoading && runList.length === 0 && feed.error === null ? (
          <EmptyState message="No runs yet." />
        ) : null}
        {runList.map((r) => (
          <RunCard key={`${r.scheduleId}-${r.runNumber}`} run={r} />
        ))}
        <LoadMoreButton onClick={feed.loadMore} isLoading={feed.isFetchingNextPage} hasMore={feed.hasMore} />
      </section>

      {/* How It Works */}
      <section className={styles.howItWorks}>
        <h3 className={styles.howItWorksTitle}>How It Works</h3>
        <ol className={styles.howItWorksList}>
          {HOW_IT_WORKS.map((step, idx) => (
            <li key={idx} className={styles.howItWorksStep}>{step}</li>
          ))}
        </ol>
      </section>
    </div>
  );
};

export { AutonomyPage };
